import React, { Fragment, useEffect, useState } from 'react';
import Carousel from 'react-multi-carousel';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import { Box, Grid, TextField, FormLabel, Typography, FormControl } from '@mui/material';

// components
import NotesList from './notesList';
import BBModal from '../../UI/BBModal';
import ICIconButton from '../../UI/IconButton';
import ICDateInput from '../../Form/DateInput';
import BBSelect from '../../Form/BBSelect';
import FileUploader from '../../UI/FileUploader';
import ViewImageModal from '../../UI/ViewImageModal';

// apis
import {
  addTreatment,
  deleteTreatmentNote,
  getTreatment,
  updateTreatment as updateTreatmentAPI,
} from '../../../api/protocols';
import { stockBatchNameList as stockBatchNameListAPI } from '../../../api/stocks';
import { usePatientWarningCheck } from '../../../hooks/usePatientWarningCheck';
import {
  FIELD_TYPES,
  FILE_TYPES,
  PROTOCOL_TYPE,
  STOCK_STATUS,
  STOCK_TYPE,
} from '../../../utils/constants/common';
import useToast from '../../../hooks/useToast';

const responsive = {
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 3,
    slidesToSlide: 3, // optional, default to 1.
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 2,
    slidesToSlide: 2, // optional, default to 1.
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 1,
    slidesToSlide: 1, // optional, default to 1.
  },
};

const AddEditTreatmentModal = ({
  open,
  onClose,
  protocolId,
  treatmentId,
  protocolType,
  protocolList,
  refreshProtocol,
}) => {
  const [fields, setFields] = useState([]);
  const [stockType, setStockType] = useState(0);
  const [treatmentImages, setTreatmentImages] = useState([]);
  const [measure, setMeasure] = useState('');
  const [area, setArea] = useState('');
  const [treatmentName, setTreatmentName] = useState('');
  const [stockBatchNameOptions, setStockBatchNameOptions] = useState([]);
  const [checkWarning, setCheckWarning] = useState('');
  const [scheduledDrugsPrescribed, setScheduledDrugsPrescribed] = useState('');
  const [notes, setNotes] = useState([]);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [selectedImg, setSelectedImg] = useState('');
  const [isNoteEmpty, setIsNoteEmpty] = useState(true);
  const { showToast } = useToast();

  const headClinicData = useSelector((state) => state.commonReducers.headClinicData);
  const patientId = useSelector((state) => state.commonReducers.prPatientData._id);

  const protocolOptions = protocolList.map((protocol) => ({
    label: protocol.name,
    ...protocol,
  }));

  const openImage = (img) => {
    setSelectedImg(img);
    setOpenImageModal(true);
  };

  const closeImage = () => {
    setOpenImageModal(false);
  };

  useEffect(() => {
    if (!treatmentId) return;

    getTreatmentById(treatmentId);
  }, [treatmentId]);

  const validateTheForm = () => {
    const validationRules = {
      clinicId: Yup.string().required('Please select the Clinic'),
      batchName: Yup.string().when('stockType', {
        is: () => stockType !== STOCK_TYPE.NO_STOCK,
        then: (schema) =>
          schema.required(
            `Please select the ${
              protocolType === PROTOCOL_TYPE.S4_DRUG ? 'Drug' : 'Premium Inventory'
            } Batch Name`
          ),
      }),
      protocolName: Yup.string()
        .required('Please select the Protocol Name')
        .notOneOf(['Select Protocol'], 'Please select the Protocol Name'),
      ...(protocolType === PROTOCOL_TYPE.S4_DRUG && {
        lotNo: Yup.string().required('Please enter the Lot Number'),
      }),
      ...(protocolType === PROTOCOL_TYPE.S4_DRUG && {
        productExpiry: Yup.string().required('Please select the Product Expiry'),
      }),
      complaint: isNoteEmpty
        ? Yup.string().trim()
        : Yup.string().trim().required('Please enter the Complaint'),
      impression: isNoteEmpty
        ? Yup.string().trim()
        : Yup.string().trim().required('Please enter the Impression'),
      plan: isNoteEmpty
        ? Yup.string().trim()
        : Yup.string().trim().required('Please enter the Plan'),
    };

    const fieldsValidator = () => {
      const getValidationSchema = (label, type) => {
        switch (type) {
          case FIELD_TYPES.OPEN_TEXT:
            return Yup.string().required(`Please enter the ${label}`);
          case FIELD_TYPES.MINS:
            return Yup.string().required(`Please enter the ${label}`);
          case FIELD_TYPES.MM:
            return Yup.string().required(`Please enter the ${label}`);
          case FIELD_TYPES.CALENDAR:
            return Yup.string().required(`Please enter the ${label}`);
          case FIELD_TYPES.DROPDOWN:
            return Yup.string().required(`Please select the option for ${label}`);
          default:
            return Yup.mixed();
        }
      };

      return fields.reduce((acc, { label, type }, index) => {
        const fieldName = `field-${index}`;
        return {
          ...acc,
          [fieldName]: getValidationSchema(label, type),
        };
      }, {});
    };

    const fieldsValidation = fieldsValidator() || {};

    return Yup.object({
      ...validationRules,
      ...fieldsValidation,
    });
  };

  const constructFieldsWithData = (data) => {
    const constructedArray = fields.map(({ label, type, value, options }, index) => ({
      label,
      type,
      options,
      value:
        (Object.prototype.hasOwnProperty.call(data, `field-${index}`)
          ? data[`field-${index}`].toString()
          : null) || value,
    }));
    return constructedArray;
  };

  const formik = useFormik({
    initialValues: {
      protocolName: 'Select Protocol',
      drugName: '',
      premiumInventoryName: '',
      headClinicId: headClinicData?._id,
      clinicId: '',
      batchName: '',
      qty: 'Quantity',
      measure: '',
      complaint: '',
      impression: '',
      plan: '',
      createdDate: moment(),
      stockId: null,
      lotNo: '',
      productExpiry: '',
    },
    validationSchema: validateTheForm(),
    onSubmit: async (values) => {
      const fields = constructFieldsWithData(values);
      values['fields'] = fields;
      if (treatmentId) {
        // update a treatment
        await updateTreatment();
      } else {
        // create a new treatment
        if (values.premiumInventoryName === 'Premium Inventory Name') {
          values['premiumInventoryName'] = '';
        }
        if (values.drugName === 'Drug Name') {
          values['drugName'] = '';
        }
        if (
          values.batchName === `Select ${protocolType ? 'Drug' : 'Premium Inventory'} Batch Name`
        ) {
          values['batchName'] = '';
        }

        values['beforePhotos'] = treatmentImages;
        values['expiryDate'] = moment(values.createdDate).add(12, 'M');
        values['measure'] = measure;
        values['area'] = area;
        values['name'] = treatmentName;
        values['stockType'] = stockType;
        await addTreatmentApi(values);
      }
    },
  });

  useEffect(() => {
    if (
      formik.values.complaint !== '' ||
      formik.values.impression !== '' ||
      formik.values.plan !== ''
    ) {
      setIsNoteEmpty(false);
    } else {
      setIsNoteEmpty(true);
    }
  }, [formik.values.complaint, formik.values.impression, formik.values.plan]);

  const handleProtocolChange = (val) => {
    const index = protocolList.findIndex((o) => o.name === val);
    const selectedProtocol = protocolList[index];

    if (selectedProtocol && selectedProtocol.premiumInventoryName) {
      formik.setFieldValue('premiumInventoryName', selectedProtocol.premiumInventoryName);
    }
    if (selectedProtocol && selectedProtocol.drugName) {
      formik.setFieldValue('drugName', selectedProtocol.drugName);
    }

    formik.setFieldValue('qty', selectedProtocol.qty);
    formik.setFieldValue('measure', selectedProtocol.measure);

    setMeasure(selectedProtocol.measure);
    setArea(selectedProtocol.area);
    setTreatmentName(selectedProtocol.treatment);

    const foundTreatment = headClinicData?.treatments?.find(
      ({ name }) => name == selectedProtocol.treatment
    );

    if (foundTreatment) {
      setFields(foundTreatment.fields);
      foundTreatment.fields.forEach((field, index) => {
        formik.setFieldValue(`field-${index}`, '');
      });
      setStockType(foundTreatment.stockType);
    }
  };

  useEffect(() => {
    if (formik.values.headClinicId && formik.values.clinicId) {
      getStockList();
    }
  }, [formik.values.clinicId]);

  const getStockList = async () => {
    try {
      const params = {
        limit: 1000,
        skip: 0,
        status: STOCK_STATUS.IN_STOCK,
        type: protocolType === PROTOCOL_TYPE.OTHER ? STOCK_TYPE.OTHER : STOCK_TYPE.INJECTABLE,
        headClinicId: formik.values.headClinicId || '',
        clinicId: formik.values.clinicId || '',
      };
      const res = await stockBatchNameListAPI(params);
      const { data } = res;
      if (data && data.length > 0) {
        const drugOrPremiumInventoryName =
          protocolType === PROTOCOL_TYPE.OTHER ? 'premiumInventoryName' : 'drugName';
        const selectedDrugOrPremiumInventoryName = formik.values[drugOrPremiumInventoryName];
        setStockBatchNameOptions(
          data
            .filter((obj) => obj.name === selectedDrugOrPremiumInventoryName)
            .map(({ batchName, _id, name }) => ({
              label: batchName,
              batchName,
              stockId: _id,
              [drugOrPremiumInventoryName]: name,
            }))
        );
      }
    } catch (error) {
      // continue regardless of error
    }
  };

  const handleTreatmentImages = (file) => {
    let tmpImages = [...treatmentImages];
    tmpImages.push(file);
    setTreatmentImages(tmpImages);
  };

  const addTreatmentApi = async (values) => {
    values.patientId = patientId;
    values.protocolType = protocolType;
    values.protocolId = protocolId;
    try {
      await addTreatment(values);
      showToast('Treatment added successfully', 'success');
      refreshProtocol();
      setCheckWarning(true);
    } catch (error) {
      showToast('Treatment could not be added', 'error');
    }
  };

  const getTreatmentById = async (treatmentId) => {
    try {
      const params = { protocolId: protocolId, treatmentId };
      const res = await getTreatment(params);
      const treatment = res.data;
      formik.setFieldValue('createdDate', moment(treatment.createdDate));
      formik.setFieldValue('protocolName', treatment.protocolName);
      formik.setFieldValue('premiumInventoryName', treatment.premiumInventoryName);
      formik.setFieldValue('drugName', treatment.drugName);
      formik.setFieldValue('batchName', treatment.batchName);
      formik.setFieldValue('qty', treatment.qty);
      formik.setFieldValue('lotNo', treatment.lotNo);
      formik.setFieldValue('productExpiry', moment(treatment.productExpiry));
      formik.setFieldValue('headClinicId', treatment.headClinicId || '');
      formik.setFieldValue('clinicId', treatment.clinicId || '');
      formik.setFieldValue('measure', treatment.measure);
      setFields(treatment.fields || []);
      treatment.fields.forEach((field, index) => {
        formik.setFieldValue(`field-${index}`, field.value);
      });
      setTreatmentName(treatment.name);
      setNotes(treatment.notes);
      setMeasure(treatment.measure);
      setArea(treatment.area);
      setStockType(treatment.stockType || 0);
      setScheduledDrugsPrescribed(
        `${treatment.drugName} (${treatment.batchName}) - ${treatment.area}: ${treatment.qty} ${treatment.measure}`
      );
      setTreatmentImages(treatment.beforePhotos);
    } catch (error) {
      // continue regardless of error
    }
  };

  const updateTreatment = async () => {
    try {
      const dataObj = {
        treatmentId: treatmentId,
        protocolId: protocolId,
        beforePhotos: treatmentImages,
        complaint: formik.values.complaint,
        impression: formik.values.impression,
        plan: formik.values.plan,
        lotNo: formik.values.lotNo,
        productExpiry: formik.values.productExpiry,
        qty: formik.values.qty,
        fields: formik.values.fields,
      };
      await updateTreatmentAPI(dataObj);
      showToast('Treatment updated successfully', 'success');
      refreshProtocol();
    } catch (error) {
      showToast('Treatment could not be updated', 'error');
    }
  };

  const deleteNote = async (noteId) => {
    try {
      const dataObj = {
        protocolId: protocolId,
        treatmentId: treatmentId,
        noteId,
      };
      await deleteTreatmentNote(dataObj);
      showToast('Treatment Note deleted successfully', 'success');
      const updatedNotes = notes.filter((note) => note._id !== noteId);
      setNotes(updatedNotes);
    } catch (error) {
      showToast('Treatment Note could not be deleted', 'error');
    }
  };

  const handleClinicChange = (val) => {
    formik.setFieldValue('clinicId', val._id);
  };

  const getClinicOptions = () => {
    const { headClinicId } = formik.values;
    if (headClinicId === headClinicData._id) {
      return headClinicData.clinicIds.map((clinic) => ({ label: clinic.name, ...clinic }));
    } else return [];
  };

  useEffect(() => {
    if (
      formik.values.protocolName !== 'Select Protocol' &&
      formik.values.batchName !== 'Select Drug Batch Name'
    )
      setScheduledDrugsPrescribed(
        `${formik.values.drugName} (${formik.values.batchName}) - ${area}: ${formik.values.qty} ${measure}`
      );
  }, [
    formik.values.protocolName,
    formik.values.batchName,
    formik.values.drugName,
    formik.values.batchName,
    formik.values.qty,
    formik.values.clinicId,
    formik.values.headClinicId,
  ]);

  const deleteImg = (imgIndex) => {
    const newTreatmentImages = [...treatmentImages];
    newTreatmentImages.splice(imgIndex, 1);
    setTreatmentImages(newTreatmentImages);
  };

  const getField = (field, index) => {
    const { label, type, options } = field;
    const name = `field-${index}`;
    if (type === FIELD_TYPES.OPEN_TEXT) {
      return (
        <Grid item sm={6} key={index}>
          <FormControl fullWidth>
            <FormLabel required>{label}</FormLabel>
            <TextField
              id={name}
              name={name}
              type="text"
              placeholder={label}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values[name]}
              error={formik.touched[name] && Boolean(formik.errors[name])}
              helperText={formik.touched[name] && formik.errors[name]}
            />
          </FormControl>
        </Grid>
      );
    } else if (type === FIELD_TYPES.MINS) {
      return (
        <Grid item sm={6} key={index}>
          <FormControl fullWidth>
            <FormLabel required>{label}</FormLabel>
            <TextField
              id={name}
              name={name}
              type="number"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values[name]}
              error={formik.touched[name] && Boolean(formik.errors[name])}
              helperText={formik.touched[name] && formik.errors[name]}
            />
          </FormControl>
        </Grid>
      );
    } else if (type === FIELD_TYPES.MM) {
      return (
        <Grid item sm={6} key={index}>
          <FormControl fullWidth>
            <FormLabel required>{label}</FormLabel>
            <TextField
              id={name}
              name={name}
              type="number"
              placeholder={label}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values[name]}
              error={formik.touched[name] && Boolean(formik.errors[name])}
              helperText={formik.touched[name] && formik.errors[name]}
            />
          </FormControl>
        </Grid>
      );
    } else if (type === FIELD_TYPES.CALENDAR) {
      return (
        <Grid item sm={6} key={index}>
          <FormControl fullWidth>
            <FormLabel required> {label}</FormLabel>
            <ICDateInput
              id={name}
              name={name}
              className="cal-primary-bg"
              defaultDate={formik.values[name]}
              handleChange={(val) => {
                formik.values[name] = val;
                formik.setFieldValue(name, moment(val));
                formik.handleChange(name);
              }}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values[name]}
              error={formik.touched[name] && Boolean(formik.errors[name])}
              helperText={formik.touched[name] && formik.errors[name]}
            />
          </FormControl>
        </Grid>
      );
    } else if (type === FIELD_TYPES.DROPDOWN) {
      return (
        <Grid item sm={6} key={index}>
          <FormControl fullWidth>
            <FormLabel required>{label}</FormLabel>
            <BBSelect
              id={name}
              name={name}
              placeholder={`Select ${label}`}
              options={options.map((val) => ({ _id: val, label: val }))}
              value={formik.values[name]}
              onBlur={formik.handleBlur}
              onChange={(val) => {
                formik.setFieldValue(name, val._id);
              }}
              error={formik.touched[name] && Boolean(formik.errors[name])}
              helperText={formik.touched[name] && formik.errors[name]}
            />
          </FormControl>
        </Grid>
      );
    }
  };

  usePatientWarningCheck(checkWarning);
  const handleCloseModal = () => {
    formik.resetForm();
    setTreatmentImages([]);
    setStockType(0);
    setFields([]);
    onClose();
  };
  return (
    <BBModal
      title={treatmentId ? 'Edit Treatment' : 'Add Treatment'}
      size="md"
      openModal={open}
      cancelEvent={handleCloseModal}
      cancelText={'Cancel'}
      okayEvent={formik.handleSubmit}
      okayText={treatmentId ? 'Update Treatment' : 'Create Treatment'}
      loadingText={treatmentId ? 'Updating...' : 'Creating...'}
      loading={formik.isSubmitting}
      modalBodyContent={
        <Fragment>
          <Box component="form" onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              <Grid item sm={6}>
                <FormControl fullWidth>
                  <FormLabel required>Date</FormLabel>
                  <ICDateInput
                    disabled={!!treatmentId}
                    id="createdDate"
                    name="createdDate"
                    className="cal-primary-bg"
                    label=""
                    defaultDate={formik.values.createdDate}
                    handleChange={(val) => {
                      formik.values.date = val;
                      formik.setFieldValue('createdDate', moment(val));
                      formik.handleChange('createdDate');
                    }}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.createdDate}
                    error={formik.touched.createdDate && Boolean(formik.errors.createdDate)}
                    helperText={formik.touched.createdDate && formik.errors.createdDate}
                  />
                </FormControl>
              </Grid>
              <Grid item sm={6}>
                <FormControl fullWidth>
                  <FormLabel required>Protocol</FormLabel>
                  <BBSelect
                    disabled={!!treatmentId}
                    placeholder="Select Protocol"
                    id="protocolName"
                    name="protocolName"
                    options={protocolOptions || []}
                    value={formik.values.protocolName}
                    onBlur={formik.handleBlur}
                    onChange={(val) => {
                      formik.setFieldValue('protocolName', val.name);
                      handleProtocolChange(val.name);
                    }}
                    error={formik.touched.protocolName && Boolean(formik.errors.protocolName)}
                    helperText={formik.touched.protocolName && formik.errors.protocolName}
                  />
                </FormControl>
              </Grid>
              <Grid item sm={6}>
                <FormControl fullWidth>
                  <FormLabel required>Select Clinic</FormLabel>
                  <BBSelect
                    placeholder="Select Clinic"
                    options={getClinicOptions()}
                    onChange={handleClinicChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.clinicId}
                    disabled={!!treatmentId || !formik.values.headClinicId}
                    error={formik.touched.clinicId && Boolean(formik.errors.clinicId)}
                    helperText={formik.touched.clinicId && formik.errors.clinicId}
                  />
                </FormControl>
              </Grid>
              {(formik.values.premiumInventoryName || stockType === STOCK_TYPE.OTHER) && (
                <Grid item sm={6}>
                  <FormControl fullWidth>
                    <FormLabel>Premium Inventory Name</FormLabel>
                    <TextField
                      id="premiumInventoryName"
                      name="premiumInventoryName"
                      type="text"
                      disabled={true}
                      placeholder="Premium Inventory Name"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.premiumInventoryName}
                      error={
                        formik.touched.premiumInventoryName &&
                        Boolean(formik.errors.premiumInventoryName)
                      }
                      helperText={
                        formik.touched.premiumInventoryName && formik.errors.premiumInventoryName
                      }
                    />
                  </FormControl>
                </Grid>
              )}
              {(formik.values.drugName || stockType === STOCK_TYPE.INJECTABLE) && (
                <Grid item sm={6}>
                  <FormControl fullWidth>
                    <FormLabel>Drug Name</FormLabel>
                    <TextField
                      id="drugName"
                      name="drugName"
                      type="text"
                      disabled={true}
                      placeholder="Drug Name"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.drugName}
                      error={formik.touched.drugName && Boolean(formik.errors.drugName)}
                      helperText={formik.touched.drugName && formik.errors.drugName}
                    />
                  </FormControl>
                </Grid>
              )}
              {[STOCK_TYPE.INJECTABLE, STOCK_TYPE.OTHER].includes(stockType) && (
                <Grid item sm={6}>
                  <FormControl fullWidth>
                    <FormLabel required>
                      {protocolType === PROTOCOL_TYPE.S4_DRUG
                        ? 'Drug Batch Name'
                        : 'Premium Inventory Batch Name'}
                    </FormLabel>
                    <BBSelect
                      id="batchName"
                      name="batchName"
                      disabled={!!treatmentId || !formik.values.clinicId}
                      placeholder={`Select ${
                        protocolType === PROTOCOL_TYPE.S4_DRUG ? 'Drug' : 'Premium Inventory'
                      } Batch Name`}
                      options={stockBatchNameOptions}
                      value={formik.values.batchName}
                      onBlur={formik.handleBlur}
                      onChange={(val) => {
                        formik.setFieldValue('batchName', val.batchName);
                        const selectedBatchName = stockBatchNameOptions.find(
                          (item) => item.batchName === val.batchName
                        );
                        formik.values.stockId = selectedBatchName.stockId;
                        formik.setFieldValue('stockId', selectedBatchName.stockId);
                      }}
                      error={formik.touched.batchName && Boolean(formik.errors.batchName)}
                      helperText={formik.touched.batchName && formik.errors.batchName}
                    />
                  </FormControl>
                </Grid>
              )}
              <Grid item sm={6}>
                <FormControl fullWidth>
                  <FormLabel>Qty</FormLabel>
                  <TextField
                    id="qty"
                    name="qty"
                    type="number"
                    placeholder="Quantity"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.qty}
                    error={formik.touched.qty && Boolean(formik.errors.qty)}
                    helperText={formik.touched.qty && formik.errors.qty}
                  />
                </FormControl>
              </Grid>
              {protocolType === PROTOCOL_TYPE.S4_DRUG && (
                <Grid item sm={6}>
                  <FormControl fullWidth>
                    <FormLabel>S4 Scheduled Drugs Prescribed</FormLabel>
                    <Typography>{scheduledDrugsPrescribed}</Typography>
                  </FormControl>
                </Grid>
              )}

              {protocolType === PROTOCOL_TYPE.OTHER && (
                <Grid item sm={6}>
                  <FormControl fullWidth>
                    <FormLabel>Measure</FormLabel>
                    <TextField
                      disabled={true}
                      id="measure"
                      name="measure"
                      type="text"
                      placeholder="Measure"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.measure}
                      error={formik.touched.measure && Boolean(formik.errors.measure)}
                      helperText={formik.touched.measure && formik.errors.measure}
                    />
                  </FormControl>
                </Grid>
              )}

              {protocolType === PROTOCOL_TYPE.S4_DRUG && (
                <>
                  <Grid item sm={6}>
                    <FormControl fullWidth>
                      <FormLabel required>Lot No</FormLabel>
                      <TextField
                        id="lotNo"
                        name="lotNo"
                        type="text"
                        placeholder="Lot No."
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.lotNo}
                        error={formik.touched.lotNo && Boolean(formik.errors.lotNo)}
                        helperText={formik.touched.lotNo && formik.errors.lotNo}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item sm={6}>
                    <FormControl fullWidth>
                      <FormLabel required>Product Expiry</FormLabel>
                      <ICDateInput
                        id="productExpiry"
                        name="productExpiry"
                        className="cal-primary-bg"
                        defaultDate={formik.values.productExpiry}
                        handleChange={(val) => {
                          formik.values.date = val;
                          formik.setFieldValue('productExpiry', moment(val));
                          formik.handleChange('productExpiry');
                        }}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.productExpiry}
                        error={formik.touched.productExpiry && Boolean(formik.errors.productExpiry)}
                        helperText={formik.touched.productExpiry && formik.errors.productExpiry}
                      />
                    </FormControl>
                  </Grid>
                </>
              )}
              <Grid item sm={12} container spacing={3}>
                {fields.map((field, index) => getField(field, index))}
              </Grid>
              <Grid item sm={6}>
                <FileUploader
                  allowedTypes={[FILE_TYPES.IMAGE]}
                  displayImg={false}
                  folderName="documents"
                  onFileUpload={(file) => handleTreatmentImages(file)}
                />
              </Grid>
              <Grid item sm={12}>
                <Carousel
                  swipeable={true}
                  draggable={false}
                  responsive={responsive}
                  transitionDuration={200}
                  arrows={true}
                  showDots={false}
                  containerClass="container"
                  dotListClass="custom-dot-list-style"
                  itemClass="carousel-item-padding-40-px">
                  {treatmentImages.map((element, index) => {
                    return (
                      <Box key={index}>
                        <img
                          src={element}
                          alt="treatment-before-img"
                          onClick={() => openImage(element)}
                          className="treatment-before-img"
                        />
                        <ICIconButton
                          icon="fas fa-times"
                          title=""
                          className="only-icon warning delete-btn"
                          clickEvent={() => deleteImg(index)}
                        />
                      </Box>
                    );
                  })}
                </Carousel>
              </Grid>
            </Grid>
            <Box mt={4}>
              <Grid container spacing={3}>
                <Grid item sm={6}>
                  <FormControl fullWidth>
                    <FormLabel>Presenting Complaint</FormLabel>
                    <TextField
                      id="complaint"
                      name="complaint"
                      type="text"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.complaint}
                      placeholder="Presenting Complaint"
                      multiline
                      style={{ marginBottom: 8 }}
                      className="ic-input text-area"
                      minRows="3"
                      error={formik.touched.complaint && Boolean(formik.errors.complaint)}
                      helperText={formik.touched.complaint && formik.errors.complaint}
                    />
                  </FormControl>
                  <FormControl fullWidth>
                    <FormLabel>Impression</FormLabel>
                    <TextField
                      id="impression"
                      name="impression"
                      type="text"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.impression}
                      placeholder="Impression"
                      multiline
                      style={{ marginBottom: 8 }}
                      className="ic-input text-area"
                      minRows="3"
                      error={formik.touched.impression && Boolean(formik.errors.impression)}
                      helperText={formik.touched.impression && formik.errors.impression}
                    />
                  </FormControl>
                  <FormControl fullWidth>
                    <FormLabel>Plan</FormLabel>
                    <TextField
                      id="plan"
                      name="plan"
                      type="text"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.plan}
                      placeholder="Plan"
                      multiline
                      style={{ marginBottom: 8 }}
                      className="ic-input text-area"
                      minRows="3"
                      error={formik.touched.plan && Boolean(formik.errors.plan)}
                      helperText={formik.touched.plan && formik.errors.plan}
                    />
                  </FormControl>
                </Grid>
                <Grid item sm={6}>
                  <NotesList notesList={notes} deleteNote={deleteNote} />
                </Grid>
              </Grid>
            </Box>
          </Box>
          <ViewImageModal
            title="Selected Image"
            open={openImageModal}
            onClose={closeImage}
            img={selectedImg}
            alt="selected-img"
          />
        </Fragment>
      }
    />
  );
};

export default AddEditTreatmentModal;
